import api from './api';

export interface NewsroomChannel {
  id: string;
  name: string;
  slug: string;
  description: string;
  color: string;
  professional_type: string | null;
  sort_order: number;
  article_count: number;
  is_subscribed?: boolean;
}

export interface NewsroomArticle {
  id: string;
  channel_id: string;
  channel_name: string;
  channel_slug: string;
  channel_color: string;
  channel_professional_type?: string | null;
  author_id: string;
  first_name: string;
  last_name: string;
  profile_photo_path?: string;
  title: string;
  slug: string;
  excerpt: string;
  content?: string;
  cover_image_path?: string;
  cover_image_url: string | null;
  external_url: string | null;
  likes_count: number;
  comments_count: number;
  views_count: number;
  published_at: string;
  created_at: string;
  comments?: NewsroomComment[];
  user_liked?: boolean;
}

export interface NewsroomComment {
  id: string;
  article_id: string;
  author_id: string;
  parent_id?: string | null;
  first_name: string;
  last_name: string;
  profile_photo_path?: string;
  content: string;
  likes_count: number;
  created_at: string;
  replies?: NewsroomComment[];
}

export interface LinkPreview {
  title: string;
  image_url: string | null;
  description: string;
  url: string;
}

export type ArticleSortBy = 'recent' | 'most_read' | 'most_discussed' | 'most_liked';

export interface NewsroomLiker {
  id: string;
  first_name: string;
  last_name: string;
  profile_photo_path?: string;
  created_at: string;
}

export const newsroomApi = {
  getChannels: async (): Promise<NewsroomChannel[]> => {
    const res = await api.get('/newsroom/channels');
    return res.data.data;
  },

  getArticles: async (params: {
    channel_id?: string;
    search?: string;
    page?: number;
    limit?: number;
    sort_by?: ArticleSortBy;
    year?: number;
  }): Promise<NewsroomArticle[]> => {
    const res = await api.get('/newsroom/articles', { params });
    return res.data.data;
  },

  getArticle: async (id: string): Promise<NewsroomArticle> => {
    const res = await api.get(`/newsroom/articles/${id}`);
    return res.data.data;
  },

  fetchPreview: async (url: string): Promise<LinkPreview> => {
    const res = await api.post('/newsroom/fetch-preview', { url });
    return res.data.data;
  },

  createArticle: async (data: {
    external_url: string;
    title: string;
    channel_id: string;
    og_image_url?: string | null;
    description?: string;
    published_at?: string;
  }): Promise<NewsroomArticle> => {
    const res = await api.post('/newsroom/articles', data);
    return res.data.data;
  },

  toggleLike: async (articleId: string): Promise<{ liked: boolean }> => {
    const res = await api.post(`/newsroom/articles/${articleId}/like`);
    return res.data;
  },

  getLikes: async (articleId: string): Promise<NewsroomLiker[]> => {
    const res = await api.get(`/newsroom/articles/${articleId}/likes`);
    return res.data.data;
  },

  addComment: async (articleId: string, content: string, parent_id?: string): Promise<NewsroomComment> => {
    const res = await api.post(`/newsroom/articles/${articleId}/comments`, { content, parent_id });
    return res.data.data;
  },

  updateArticle: async (id: string, data: { title?: string; channel_id?: string; published_at?: string }): Promise<void> => {
    await api.put(`/newsroom/articles/${id}`, data);
  },

  deleteArticle: async (id: string): Promise<void> => {
    await api.delete(`/newsroom/articles/${id}`);
  },

  deleteComment: async (articleId: string, commentId: string): Promise<void> => {
    await api.delete(`/newsroom/articles/${articleId}/comments/${commentId}`);
  },

  subscribeChannel: async (channelId: string): Promise<void> => {
    await api.post(`/newsroom/channels/${channelId}/subscribe`);
  },

  unsubscribeChannel: async (channelId: string): Promise<void> => {
    await api.delete(`/newsroom/channels/${channelId}/subscribe`);
  },

  getSubscriptions: async (): Promise<string[]> => {
    const res = await api.get('/newsroom/subscriptions');
    return res.data.data;
  },
};
